import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { calculateRelativePosition, getWordBackgroundColor, type WordData, type BlockData } from '../config/wordOverlayConfig';
import { useWordDataContext } from '../contexts/WordDataContext';

// Track logged image dimensions per page/resize to avoid duplicate logs
const loggedImageDimensions = new Set<string>();

interface WordOverlayContainerProps {
  word: WordData;
  block: BlockData;
  pageIndex: number;
  imageDimensions: { width: number; height: number };
  isHighlighted: boolean;
  onClick: (word: WordData, block: BlockData) => void;
}

const WordOverlay: React.FC<WordOverlayContainerProps> = ({ word, block, pageIndex, imageDimensions, isHighlighted, onClick }) => {
  const { state, actions } = useWordDataContext();
  const [localText, setLocalText] = useState(word.text);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wordSpanRef = useRef<HTMLSpanElement>(null);
  const measuredPositionRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  
  const editMode = state.editMode;
  const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
  const isSelected = state.selectedWord?.wordId === String(word.wordId) && state.selectedWord?.pageIndex === pageIndex;
  
  // Get the current word from state (which has updated boundingBox) instead of using prop
  const blocks = state.blocksByPage.get(pageIndex) || [];
  const currentBlock = blocks.find(b => {
    const bId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
    return bId === blockIdNum;
  });
  const currentWord = currentBlock?.words.find(w => w.wordId === word.wordId) || word;
  
  // Calculate word index in the array
  const wordIndex = block.words.findIndex(w => w.wordId === word.wordId);
  
  // Sync local text when word.text changes from context
  useEffect(() => {
    setLocalText(currentWord.text);
  }, [currentWord.text]);
  
  // Focus input when entering text edit mode
  useEffect(() => {
    if (editMode === 'text' && isEditingText && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editMode, isEditingText]);
  
  // Auto-enter edit mode when word is selected and edit mode is 'text' (for Tab navigation)
  useEffect(() => {
    if (editMode === 'text' && isSelected && !isEditingText && inputRef.current) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        setIsEditingText(true);
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [editMode, isSelected, isEditingText]);
  
  const wordPosition = calculateRelativePosition(currentWord.boundingBox, block.boundingBox);
  const wordBgColor = getWordBackgroundColor(currentWord.confidence);

  // Determine if we should show Rnd (for resize or move modes) - define early for useLayoutEffect
  const shouldShowRnd = editMode === 'resize' || editMode === 'move';

  // Measure word position when rendered normally (not in Rnd mode)
  // Use useLayoutEffect to measure synchronously before React paints
  useLayoutEffect(() => {
    if (!shouldShowRnd && wordSpanRef.current) {
      const overlayContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
      if (overlayContainer && wordSpanRef.current) {
        const wordRect = wordSpanRef.current.getBoundingClientRect();
        const containerRect = overlayContainer.getBoundingClientRect();
        
        measuredPositionRef.current = {
          x: wordRect.left - containerRect.left,
          y: wordRect.top - containerRect.top,
          width: wordRect.width,
          height: wordRect.height,
        };
      }
    }
  }, [shouldShowRnd, pageIndex, wordPosition, currentWord.boundingBox]);

  // Log word position on page load and resize
  useEffect(() => {
    if (!shouldShowRnd && wordSpanRef.current) {
      const overlayContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
      if (overlayContainer && wordSpanRef.current) {
        const containerRect = overlayContainer.getBoundingClientRect();
        const resizedWidth = containerRect.width;
        const resizedHeight = containerRect.height;
        const originalWidth = imageDimensions.width;
        const originalHeight = imageDimensions.height;
        
        // Log image dimensions once per page/resize (using Set to track across all word components)
        const imageKey = `${pageIndex}-${resizedWidth}-${resizedHeight}`;
        if (!loggedImageDimensions.has(imageKey)) {
          const resizeFactorX = resizedWidth / originalWidth;
          const resizeFactorY = resizedHeight / originalHeight;
          const resizeFactor = (resizeFactorX + resizeFactorY) / 2; // Average factor
          
          console.log(`image; original-width: ${originalWidth}, original-height: ${originalHeight}, resized-width: ${resizedWidth}, resized-height: ${resizedHeight}, resize-factor: ${resizeFactor.toFixed(4)}`);
          
          loggedImageDimensions.add(imageKey);
        }
        
        const wordRect = wordSpanRef.current.getBoundingClientRect();
        const resizedLeft = wordRect.left - containerRect.left;
        const resizedTop = wordRect.top - containerRect.top;
        
        // Calculate original coordinates from resized coordinates
        const scaleToOriginalX = originalWidth / resizedWidth;
        const scaleToOriginalY = originalHeight / resizedHeight;
        const originalLeft = resizedLeft * scaleToOriginalX;
        const originalTop = resizedTop * scaleToOriginalY;
        
        console.log(`Page-index: ${pageIndex}, block-id: ${block.blockId}, word-id: ${word.wordId}, left: ${resizedLeft}, top: ${resizedTop}, original-left: ${originalLeft.toFixed(2)}, original-top: ${originalTop.toFixed(2)}, word: ${currentWord.text}`);
      }
    }
  }, [shouldShowRnd, pageIndex, imageDimensions, block.blockId, word.wordId, currentWord.text]);

  // Calculate word position in pixels for Rnd
  // IMPORTANT: Use measured position if available, otherwise measure the actual word element
  const getWordPositionInPixels = useCallback(() => {
    // If we have a measured position from when the word was rendered normally, use it
    if (measuredPositionRef.current) {
      return measuredPositionRef.current;
    }
    
    const overlayContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
    if (!overlayContainer) return { x: 0, y: 0, width: 0, height: 0 };
    
    // Try to find and measure the actual word element
    const blockElement = overlayContainer.querySelector(`.overlay-block[data-block-id="${block.blockId}"]`);
    if (blockElement) {
      const wordElement = blockElement.querySelector(`.overlay-word[data-word-id="${word.wordId}"]`) as HTMLElement;
      
      if (wordElement) {
        const wordRect = wordElement.getBoundingClientRect();
        const containerRect = overlayContainer.getBoundingClientRect();
        
        return {
          x: wordRect.left - containerRect.left,
          y: wordRect.top - containerRect.top,
          width: wordRect.width,
          height: wordRect.height,
        };
      }
    }
    
    // Fallback: calculate from boundingBox
    const containerWidth = overlayContainer.clientWidth;
    const containerHeight = overlayContainer.clientHeight;
    
    const scaleX = containerWidth / imageDimensions.width;
    const scaleY = containerHeight / imageDimensions.height;
    
    return {
      x: currentWord.boundingBox.x * scaleX,
      y: currentWord.boundingBox.y * scaleY,
      width: currentWord.boundingBox.width * scaleX,
      height: currentWord.boundingBox.height * scaleY,
    };
  }, [pageIndex, currentWord, block, imageDimensions]);

  const wordRgbMatch = wordBgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  const wordBorderColor = isHighlighted ? '#ffff00' : 
    wordRgbMatch ? `rgb(${wordRgbMatch[1]}, ${wordRgbMatch[2]}, ${wordRgbMatch[3]})` : '#000000';

  // Handle click based on edit mode
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    console.log('[WordOverlay] Click detected, editMode:', editMode, 'overlayContainer:', (e.currentTarget as HTMLElement).closest('.word-overlay-container'));
    
    // If overlays are hidden, turn them on first
    const overlayContainer = (e.currentTarget as HTMLElement).closest('.word-overlay-container');
    if (overlayContainer?.classList.contains('hide-overlays')) {
      console.log('[WordOverlay] Overlays are hidden, enabling them');
      // Remove hide-overlays class from all containers
      const allContainers = document.querySelectorAll('.word-overlay-container');
      allContainers.forEach(container => {
        container.classList.remove('hide-overlays');
      });
      // Trigger the overlay toggle in SpreadEditor by dispatching a custom event
      window.dispatchEvent(new CustomEvent('enableOverlays'));
    }
    
    if (editMode === 'text') {
      setIsEditingText(true);
      const wordIdStr = String(word.wordId);
      console.log('[WordOverlay] handleClick - Setting selected word:', { 
        wordId: word.wordId, 
        wordIdStr, 
        pageIndex,
        editMode 
      });
      actions.setSelectedWord(wordIdStr, pageIndex);
      console.log('[WordOverlay] handleClick - Called setSelectedWord');
    } else if (editMode === 'delete-word') {
      actions.deleteWordFromBlock(pageIndex, blockIdNum, word.wordId);
    } else {
      // Not in text edit mode - flash the word
      const wordIdStr = String(word.wordId);
      console.log('[WordOverlay] Setting flashing word:', { wordId: word.wordId, wordIdStr, pageIndex, editMode });
      actions.setFlashingWord(wordIdStr, pageIndex);
      // Clear flash after 3 seconds (6 flashes at 0.5s each)
      setTimeout(() => {
        console.log('[WordOverlay] Clearing flashing word');
        actions.setFlashingWord(null);
      }, 3000);
      onClick(word, block);
    }
  };
  
  // Handle double-click to enter edit-text mode
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If overlays are hidden, turn them on first
    const overlayContainer = (e.currentTarget as HTMLElement).closest('.word-overlay-container');
    if (overlayContainer?.classList.contains('hide-overlays')) {
      // Remove hide-overlays class from all containers
      const allContainers = document.querySelectorAll('.word-overlay-container');
      allContainers.forEach(container => {
        container.classList.remove('hide-overlays');
      });
      // Trigger the overlay toggle in SpreadEditor by dispatching a custom event
      window.dispatchEvent(new CustomEvent('enableOverlays'));
    }
    
    // Set edit mode to text and enable editing for this word
    actions.setEditMode('text');
    setIsEditingText(true);
    actions.setSelectedWord(String(word.wordId), pageIndex);
  };
  
  // Update context in real-time as user types (with debouncing)
  const updateContextWithDebounce = useCallback((newText: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      actions.updateWordText(pageIndex, blockIdNum, word.wordId, newText);
    }, 150);
  }, [pageIndex, blockIdNum, word.wordId, actions]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setLocalText(newText);
    updateContextWithDebounce(newText);
  };
  
  const handleBlur = () => {
    actions.updateWordText(pageIndex, blockIdNum, word.wordId, localText);
    setIsEditingText(false);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    
    if (e.key === 'Escape') {
      setIsEditingText(false);
      e.currentTarget.blur();
    } else if (e.key === 'Enter') {
      setIsEditingText(false);
      e.currentTarget.blur();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      
      // Get all words from all blocks on this page, sorted by position
      const allWords: Array<{ word: WordData; block: BlockData; blockId: number }> = [];
      blocks.forEach(b => {
        const bId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
        b.words.forEach(w => {
          allWords.push({ word: w, block: b, blockId: bId });
        });
      });
      
      // Sort words by position: top to bottom, then left to right
      allWords.sort((a, b) => {
        const aY = a.word.boundingBox.y;
        const bY = b.word.boundingBox.y;
        if (Math.abs(aY - bY) > 5) { // If Y difference is significant (more than 5px), sort by Y
          return aY - bY;
        }
        // Otherwise sort by X (left to right)
        return a.word.boundingBox.x - b.word.boundingBox.x;
      });
      
      // Find current word index
      const currentIndex = allWords.findIndex(w => w.word.wordId === word.wordId);
      if (currentIndex === -1) return;
      
      // Determine target index
      let targetIndex: number;
      if (e.shiftKey) {
        // Shift+Tab: go to previous word
        targetIndex = currentIndex - 1;
        if (targetIndex < 0) {
          // Wrap to last word
          targetIndex = allWords.length > 0 ? allWords.length - 1 : 0;
        }
      } else {
        // Tab: go to next word
        targetIndex = currentIndex + 1;
        if (targetIndex >= allWords.length) {
          // Wrap to first word
          targetIndex = 0;
        }
      }
      
      const targetWordData = allWords[targetIndex];
      if (targetWordData) {
        // Set the target word as selected
        actions.setSelectedWord(String(targetWordData.word.wordId), pageIndex);
        
        // Find the input element for the target word and focus it
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
          const targetInput = document.querySelector(
            `input.word-edit-input[data-word-id="${targetWordData.word.wordId}"]`
          ) as HTMLInputElement;
          
          // The useEffect will automatically enter edit mode when the word is selected
          // Just focus the input if it exists, otherwise wait for it to render
          setTimeout(() => {
            const targetInput = document.querySelector(
              `input.word-edit-input[data-word-id="${targetWordData.word.wordId}"]`
            ) as HTMLInputElement;
            
            if (targetInput) {
              targetInput.focus();
              targetInput.select();
            }
          }, 50);
        }, 0);
      }
    }
  };

  // Handle drag stop to update word position
  const handleDragStop = useCallback((e: any, d: { x: number; y: number }) => {
    const overlayContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
    if (!overlayContainer) return;
    
    const parentWidth = overlayContainer.clientWidth;
    const parentHeight = overlayContainer.clientHeight;
    
    const scaleX = imageDimensions.width / parentWidth;
    const scaleY = imageDimensions.height / parentHeight;
    
    const newX = d.x * scaleX;
    const newY = d.y * scaleY;
    
    // Clear measured position ref so it recalculates with new boundingBox
    measuredPositionRef.current = null;
    
    actions.updateWordPosition(pageIndex, blockIdNum, currentWord.wordId, {
      x: newX,
      y: newY,
      width: currentWord.boundingBox.width,
      height: currentWord.boundingBox.height
    });
  }, [pageIndex, blockIdNum, currentWord.wordId, currentWord.boundingBox.width, currentWord.boundingBox.height, imageDimensions, actions]);
  
  // Handle resize stop to update word size
  const handleResizeStop = useCallback((e: any, direction: any, ref: HTMLElement, delta: any, position: { x: number; y: number }) => {
    const overlayContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
    if (!overlayContainer) return;
    
    const parentWidth = overlayContainer.clientWidth;
    const parentHeight = overlayContainer.clientHeight;
    
    const scaleX = imageDimensions.width / parentWidth;
    const scaleY = imageDimensions.height / parentHeight;
    
    const newX = position.x * scaleX;
    const newY = position.y * scaleY;
    const newWidth = ref.offsetWidth * scaleX;
    const newHeight = ref.offsetHeight * scaleY;
    
    // Clear measured position ref so it recalculates with new boundingBox
    measuredPositionRef.current = null;
    
    actions.updateWordPosition(pageIndex, blockIdNum, currentWord.wordId, {
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight
    });
  }, [pageIndex, blockIdNum, currentWord.wordId, imageDimensions, actions]);

  // Determine cursor style based on edit mode
  const getCursorStyle = () => {
    if (editMode === 'text') return 'text';
    if (editMode === 'move') return 'move';
    if (editMode === 'delete-word' || editMode === 'delete-block') return 'pointer';
    if (editMode === 'resize') return 'default';
    return 'pointer';
  };

  // Determine if we should show input (text mode and clicked)
  const shouldShowInput = editMode === 'text' && isEditingText;
  
  const wordContent = shouldShowInput ? (
    <div 
      style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: '4px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && (
        <div className={`word-index-badge ${isSelected ? 'selected' : ''}`}>
          {wordIndex + 1}
        </div>
      )}
      <input
        ref={inputRef}
        type="text"
        value={localText}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
        onFocus={(e) => {
          e.stopPropagation();
          setIsEditingText(true);
        }}
        data-word-id={String(word.wordId)}
        className="word-edit-input"
        style={{ 
          width: '100%', 
          height: '100%',
          pointerEvents: 'auto',
          cursor: 'text'
        }}
      />
    </div>
  ) : (
    currentWord.text
  );

  // Render Rnd in a portal for resize/move modes
  const rndContent = useMemo(() => {
    if (!shouldShowRnd) return null;
    
    const pixelPos = getWordPositionInPixels();
    const overlayContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
    
    if (!overlayContainer) return null;
    
    // Build Rnd props conditionally to avoid React warnings about props on DOM elements
    const rndProps: any = {
      position: { x: pixelPos.x, y: pixelPos.y },
      size: { width: pixelPos.width, height: pixelPos.height },
      bounds: "parent",
      cancel: ".word-edit-input",
      style: {
        zIndex: 10000,
        cursor: getCursorStyle(),
        boxSizing: 'border-box',
        border: 'none',
        padding: 0,
        margin: 0,
        outline: 'none',
      },
      resizeHandleStyles: {
        top: { height: '8px', top: '-4px', cursor: 'ns-resize' },
        right: { width: '8px', right: '-4px', cursor: 'ew-resize' },
        bottom: { height: '8px', bottom: '-4px', cursor: 'ns-resize' },
        left: { width: '8px', left: '-4px', cursor: 'ew-resize' },
        topRight: { width: '12px', height: '12px', right: '-6px', top: '-6px', cursor: 'nesw-resize' },
        bottomRight: { width: '12px', height: '12px', right: '-6px', bottom: '-6px', cursor: 'nwse-resize' },
        bottomLeft: { width: '12px', height: '12px', left: '-6px', bottom: '-6px', cursor: 'nesw-resize' },
        topLeft: { width: '12px', height: '12px', left: '-6px', top: '-6px', cursor: 'nwse-resize' },
      },
    };

    // Use disable props to control dragging/resizing (only pass when true to avoid React warnings)
    if (editMode === 'move') {
      rndProps.disableResizing = true;
      rndProps.onDragStop = handleDragStop;
    } else if (editMode === 'resize') {
      rndProps.disableDragging = true;
      rndProps.onResizeStop = handleResizeStop;
    } else {
      rndProps.disableDragging = true;
      rndProps.disableResizing = true;
    }

    rndProps.onClick = handleClick;
    rndProps.onDoubleClick = handleDoubleClick;

    return createPortal(
      <Rnd {...rndProps}>
        <span
          className="overlay-word"
          data-word-id={String(word.wordId)}
          data-word-text={localText}
          data-highlighted={String(isHighlighted)}
          data-selected={String(isSelected)}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          style={{
            '--word-bg-color': wordBgColor,
            '--word-border-color': wordBorderColor,
            width: '100%',
            height: '100%',
            cursor: getCursorStyle(),
          } as React.CSSProperties}
        >
          {wordContent}
        </span>
      </Rnd>,
      overlayContainer as HTMLElement
    );
  }, [shouldShowRnd, editMode, pageIndex, getWordPositionInPixels, handleDragStop, handleResizeStop, handleClick, word.wordId, currentWord.boundingBox, localText, isHighlighted, wordBgColor, wordBorderColor, wordContent, isSelected, getCursorStyle]);

  if (shouldShowRnd) {
    return rndContent;
  }

  return (
    <span
      ref={wordSpanRef}
      className="overlay-word"
      data-word-id={String(word.wordId)}
      data-word-text={localText}
      data-highlighted={String(isHighlighted)}
      data-selected={String(isSelected)}
      style={{
        '--word-left': `${wordPosition.left}%`,
        '--word-top': `${wordPosition.top}%`,
        '--word-width': `${wordPosition.width}%`,
        '--word-height': `${wordPosition.height}%`,
        '--word-bg-color': wordBgColor,
        '--word-border-color': wordBorderColor,
        cursor: getCursorStyle(),
      } as React.CSSProperties}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {wordContent}
    </span>
  );
};

export default WordOverlay;
